import type {
  AssignmentDTO,
  ChildDetail,
  ChildHistoryResponse,
  ChildMemRecording,
  ChildProfileResponse,
  ChildPublic,
  ChildSession,
  ChildTodayRecitation,
  FeedItem,
  MemorizationConfig,
  ModuleType,
  ParentMessage,
  ParentOverview,
  PrayerTime,
  RecitationConfig,
  SettingsResponse,
  SurahMeta,
  TodayResponse,
  WeekResponse,
} from './types';

export class ApiError extends Error {
  status: number;
  payload: unknown;
  retry_after_seconds?: number;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
    if (typeof payload === 'object' && payload && 'retry_after_seconds' in payload) {
      this.retry_after_seconds = (payload as { retry_after_seconds: number }).retry_after_seconds;
    }
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
  };
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`/api${url}`, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      typeof json === 'object' && json && 'error' in json ? (json as { error: string }).error : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, json);
  }
  return json as T;
}

export const api = {
  // Auth
  childrenList: (): Promise<{ children: ChildPublic[] }> => request('POST', '/auth/children/list'),
  loginChild: (child_id: number, pin: string): Promise<{ child: ChildSession }> =>
    request('POST', '/auth/child/login', { child_id, pin }),
  loginParent: (pin: string): Promise<{ role: 'parent' }> =>
    request('POST', '/auth/parent/login', { pin }),
  logout: (): Promise<void> => request('POST', '/auth/logout'),
  me: (): Promise<
    | { role: 'child'; child: ChildSession }
    | { role: 'parent' }
  > => request('GET', '/auth/me'),

  // Child
  today: (): Promise<TodayResponse> => request('GET', '/me/today'),
  setCurrentSurah: (surah_number: number): Promise<{ current_surah_number: number }> =>
    request('POST', '/me/current-surah', { surah_number }),
  myMessages: (unseenOnly?: boolean): Promise<{ messages: ParentMessage[] }> =>
    request('GET', `/me/messages${unseenOnly ? '?unseen_only=true' : ''}`),
  markMessageSeen: (id: number): Promise<void> => request('POST', `/me/messages/${id}/seen`),
  myTodayRecitations: (): Promise<{ quran_day: string; recitations: ChildTodayRecitation[] }> =>
    request('GET', '/me/today/recitations'),
  myTodayMemorization: (): Promise<{ mem_day: string; recordings: ChildMemRecording[] }> =>
    request('GET', '/me/today/memorization'),
  myProfile: (): Promise<ChildProfileResponse> => request('GET', '/me/profile'),
  myHistory: (params?: { limit?: number; cursor?: string }): Promise<ChildHistoryResponse> => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return request('GET', `/me/history${qs ? `?${qs}` : ''}`);
  },

  uploadRecitation: async (form: FormData): Promise<{ id: number; uuid: string; recorded_at: string; duration_seconds: number }> => {
    const res = await fetch('/api/recitations', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      const message = json && typeof json === 'object' && 'error' in json ? (json as { error: string }).error : `HTTP ${res.status}`;
      throw new ApiError(res.status, message, json);
    }
    return json;
  },

  audioUrl: (recitationId: number): string => `/api/recitations/${recitationId}/audio`,

  uploadMemorization: async (
    form: FormData,
  ): Promise<{ id: number; uuid: string; recorded_at: string; duration_seconds: number; rep_index: number }> => {
    const res = await fetch('/api/memorization-recordings', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      const message =
        json && typeof json === 'object' && 'error' in json ? (json as { error: string }).error : `HTTP ${res.status}`;
      throw new ApiError(res.status, message, json);
    }
    return json;
  },

  memorizationAudioUrl: (id: number): string => `/api/memorization-recordings/${id}/audio`,

  completePrayer: (
    prayer_time: PrayerTime,
    was_skipped: boolean,
  ): Promise<{
    prayer_time: PrayerTime;
    was_skipped: boolean;
    completed_at: string;
    next_current_prayer: PrayerTime | null;
    surah_just_completed: boolean;
  }> => request('POST', `/prayers/${prayer_time}/complete`, { was_skipped }),

  reopenPrayer: (
    prayer_time: PrayerTime,
  ): Promise<{ prayer_time: PrayerTime; next_current_prayer: PrayerTime | null }> =>
    request('POST', `/prayers/${prayer_time}/reopen`),

  completeSurah: (surah_number: number): Promise<{
    completed_surah: { number: number; name_uz: string; name_ar: string };
    next_surah: { number: number; name_uz: string; name_ar: string } | null;
    total_recitations_for_surah: number;
    days_spent_on_surah: number;
  }> => request('POST', '/surah/complete', { surah_number }),

  // Meta
  surahs: async (): Promise<{ surahs: SurahMeta[] }> => {
    const res = await fetch('/quran-meta.json');
    if (res.ok) return res.json();
    return request('GET', '/meta/surahs');
  },

  // Parent
  parentOverview: (): Promise<ParentOverview> => request('GET', '/parent/overview'),
  parentFeed: (params?: {
    limit?: number;
    cursor?: string;
    child_id?: number;
    unheard_only?: boolean;
    from_date?: string;
    to_date?: string;
  }): Promise<{ items: FeedItem[]; next_cursor: string | null }> => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.cursor) q.set('cursor', params.cursor);
    if (params?.child_id) q.set('child_id', String(params.child_id));
    if (params?.unheard_only) q.set('unheard_only', 'true');
    if (params?.from_date) q.set('from_date', params.from_date);
    if (params?.to_date) q.set('to_date', params.to_date);
    const qs = q.toString();
    return request('GET', `/parent/feed${qs ? `?${qs}` : ''}`);
  },
  markHeard: (module: 'recitation' | 'memorization', id: number, heard: boolean): Promise<void> =>
    request('POST', `/parent/recordings/${module}/${id}/heard`, { heard }),
  deleteRecording: (module: 'recitation' | 'memorization', id: number): Promise<void> =>
    request('DELETE', `/parent/recordings/${module}/${id}`),
  childDetail: (id: number): Promise<ChildDetail> => request('GET', `/parent/children/${id}`),
  childHistory: (id: number, params?: { limit?: number; cursor?: string }): Promise<{
    days: Array<{
      quran_day: string;
      prayers: Array<{
        prayer_time: PrayerTime;
        status: 'done' | 'skipped' | 'in_progress' | 'not_started';
        recitations: Array<{
          id: number;
          surah_number: number;
          surah_name_uz: string;
          duration: number;
          recorded_at: string;
        }>;
      }>;
    }>;
    next_cursor: string | null;
  }> => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return request('GET', `/parent/children/${id}/history${qs ? `?${qs}` : ''}`);
  },
  week: (start_date: string): Promise<WeekResponse> =>
    request('GET', `/parent/week?start_date=${start_date}`),
  sendMessage: (
    child_id: number,
    message_type: 'text' | 'emoji' | 'quick_phrase',
    content: string,
  ): Promise<{ id: number; sent_at: string }> =>
    request('POST', '/parent/messages', { child_id, message_type, content }),
  parentSettings: (): Promise<SettingsResponse> => request('GET', '/parent/settings'),
  patchChild: (id: number, body: Partial<{
    name: string;
    age: number;
    color_ramp: 'teal' | 'blue' | 'coral' | 'purple' | 'amber';
    avatar_initial: string;
    daily_rule_text: string;
    current_surah_number: number;
    target_per_prayer: number;
  }>): Promise<void> => request('PATCH', `/parent/settings/children/${id}`, body),
  createChild: (body: {
    name: string;
    age: number;
    color_ramp: 'teal' | 'blue' | 'coral' | 'purple' | 'amber';
    avatar_initial: string;
    pin: string;
    daily_rule_text?: string;
    target_per_prayer?: number;
    current_surah_number?: number;
  }): Promise<{ id: number; slug: string }> =>
    request('POST', '/parent/settings/children', body),
  deleteChild: (id: number): Promise<void> =>
    request('DELETE', `/parent/settings/children/${id}`),
  updateAssignment: (
    childId: number,
    moduleType: ModuleType,
    body: { is_active?: boolean; config?: RecitationConfig | MemorizationConfig },
  ): Promise<AssignmentDTO> =>
    request('PATCH', `/parent/settings/children/${childId}/assignments/${moduleType}`, body),
  uploadChildAvatar: async (id: number, file: File): Promise<{ avatar_url: string }> => {
    const form = new FormData();
    form.append('avatar', file);
    const res = await fetch(`/api/parent/settings/children/${id}/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      const message =
        json && typeof json === 'object' && 'error' in json
          ? (json as { error: string }).error
          : `HTTP ${res.status}`;
      throw new ApiError(res.status, message, json);
    }
    return json;
  },
  deleteChildAvatar: (id: number): Promise<void> =>
    request('DELETE', `/parent/settings/children/${id}/avatar`),
  setChildPin: (id: number, new_pin: string): Promise<void> =>
    request('POST', `/parent/settings/children/${id}/pin`, { new_pin }),
  setParentPin: (current_pin: string, new_pin: string): Promise<void> =>
    request('POST', '/parent/settings/pin', { current_pin, new_pin }),
  getRestDays: (from: string, to: string): Promise<{ rest_days: Array<{ child_id: number | null; quran_day: string; reason: string | null }> }> =>
    request('GET', `/parent/settings/rest-days?from=${from}&to=${to}`),
  setRestDay: (body: {
    quran_day: string;
    child_id?: number | null;
    reason?: string;
    action: 'add' | 'remove';
  }): Promise<void> => request('POST', '/parent/settings/rest-days', body),
  deletePrayerCompletion: (
    prayer_time: PrayerTime,
    child_id: number,
    quran_day: string,
  ): Promise<void> =>
    request(
      'DELETE',
      `/prayers/${prayer_time}/complete?child_id=${child_id}&quran_day=${quran_day}`,
    ),
  startExport: (): Promise<{ download_url: string; expires_at: string; size_bytes: number }> =>
    request('POST', '/parent/export'),
};
